import { doc, setDoc, serverTimestamp, collection, query, where, getDocs, arrayUnion, deleteField } from 'firebase/firestore';
import { db } from '../../../firebase';

// Create a short, opaque, deterministic ID from buyer+seller emails
// Uses SHA-256 base64url (first 20 chars) when SubtleCrypto is available,
// otherwise falls back to a stable JS hash encoded in base36.
async function generateOpaquePairId(buyerEmail, sellerEmail) {
	const input = `${buyerEmail}__${sellerEmail}`;
	try {
		if (typeof crypto !== 'undefined' && crypto.subtle && crypto.subtle.digest) {
			const enc = new TextEncoder();
			const digest = await crypto.subtle.digest('SHA-256', enc.encode(input));
			const bytes = new Uint8Array(digest);
			let bin = '';
			for (let i = 0; i < bytes.length; i++) bin += String.fromCharCode(bytes[i]);
			const b64 = btoa(bin)
				.replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/g, '');
			return b64.slice(0, 20);
		}
	} catch (_) {}
	let h1 = 5381, h2 = 52711;
	for (let i = 0; i < input.length; i++) {
		const c = input.charCodeAt(i);
		h1 = ((h1 << 5) + h1) ^ c;
		h2 = ((h2 << 5) + h2) ^ (c + 1315423911);
	}
	const combined = (Math.abs(h1) * 4294967291 + Math.abs(h2)) >>> 0;
	return combined.toString(36).padStart(8, '0').slice(0, 12);
}

// product: { id, name, image, ... }
// buyer: { uid, email, name }
// seller: { uid, email, name }
// adminEmail: string (default 'ytproudltd@gmail.com')
export async function createOrGetProductChat(product, buyer, seller, adminEmail = 'ytproudltd@gmail.com') {
  try {
    const conversationsRef = collection(db, 'sellerbuyerconversations');
    const buyerEmail = (buyer.email || '').toLowerCase();
    const sellerEmail = (seller.email || '').toLowerCase();
    const buyerSellerKey = `${buyerEmail}_${sellerEmail}`;
    
    const buyerNorm = { ...buyer, email: buyerEmail };
    const sellerNorm = { ...seller, email: sellerEmail };
    
    const newProduct = {
      id: product?.id || 'unknown'
    };

    // Find any existing conversation for this pair (any type)
    const q = query(conversationsRef, where('buyerSellerKey', '==', buyerSellerKey));
    const querySnapshot = await getDocs(q);

    let transactionConv = null;
    let productConv = null;
    querySnapshot.forEach((docSnap) => {
      const data = docSnap.data();
      if (data.type === 'transaction') transactionConv = { id: docSnap.id, ...data };
      else if (data.type === 'product_chat') productConv = { id: docSnap.id, ...data };
    });

    if (transactionConv) {
      // Attach product meta to existing transaction convo
      const docRef = doc(db, 'sellerbuyerconversations', transactionConv.id);
      const existingProducts = Array.isArray(transactionConv.products) ? transactionConv.products : [];
      // Check if product already exists in array
      const alreadyExists = existingProducts.some(p => p.id === newProduct.id);
      const updatedData = {
        product_id: newProduct.id,
        lastContactedAt: serverTimestamp(),
        products: deleteField(),
      };
      await setDoc(docRef, updatedData, { merge: true });
      return transactionConv.id;
    } else if (productConv) {
      // Reuse existing product chat, attach product meta
      const docRef = doc(db, 'sellerbuyerconversations', productConv.id);
      const existingProducts = Array.isArray(productConv.products) ? productConv.products : [];
      const alreadyExists = existingProducts.some(p => p.id === newProduct.id);
      const updatedData = {
        product_id: newProduct.id,
        lastContactedAt: serverTimestamp(),
        products: deleteField(),
      };
      await setDoc(docRef, updatedData, { merge: true });
      return productConv.id;
    } else {
      // Create new conversation with opaque deterministic ID for race-safety
      const pairId = await generateOpaquePairId(buyerEmail, sellerEmail);
      const conversationData = {
        members: [buyerEmail, sellerEmail, (adminEmail || '').toLowerCase()],
        buyer: buyerNorm,
        seller: sellerNorm,
        admin: { email: adminEmail, name: 'Admin' },
        buyerSellerKey,
        product_id: newProduct.id,
        type: 'product_chat',
        conversationState: 'product_chat',
        createdAt: serverTimestamp(),
        lastMessage: '',
        lastMessageTime: null,
        seller_username: sellerNorm.name || sellerNorm.username || 'unknown',
      };
      await setDoc(doc(db, 'sellerbuyerconversations', pairId), conversationData, { merge: true });
      return pairId;
    }
  } catch (error) {
    throw error;
  }
}

export async function createTransactionConversation({
  admin,
  buyer,
  seller,
  product,
  orderStatus,
  category_name,
  order_id = '',
  order_total = '',
  order_date = '',
}) {
  try {
    const buyerEmail = (buyer.email || '').toLowerCase();
    const sellerEmail = (seller.email || '').toLowerCase();
    const buyerSellerKey = `${buyerEmail}_${sellerEmail}`;
    const conversationsRef = collection(db, 'sellerbuyerconversations');
    // Query once for any conversation with this pair
    let q = query(conversationsRef, where('buyerSellerKey', '==', buyerSellerKey));
    let querySnapshot = await getDocs(q);
    let transactionConv = null;
    let productConv = null;
    querySnapshot.forEach((docSnap) => {
      const data = docSnap.data();
      if (data.type === 'transaction') transactionConv = { id: docSnap.id, ...data };
      else if (data.type === 'product_chat') productConv = { id: docSnap.id, ...data };
    });

    const buyerNorm = { ...buyer, email: buyerEmail };
    const sellerNorm = { ...seller, email: sellerEmail };
    
    // Prepare minimal fields; details fetched from API by IDs
    const conversationData = {
      buyer: {
        buyer_username: buyerNorm.buyer_username || '',
        email: buyerNorm.email,
        name: buyerNorm.name || '',
        uid: buyerNorm.uid || ''
      },
      seller: {
        email: sellerNorm.email,
        name: sellerNorm.name || '',
        seller_username: sellerNorm.seller_username || '',
        uid: sellerNorm.uid || ''
      },
      buyerSellerKey,
              members: [
          buyerEmail,
          sellerEmail,
          (admin?.email || 'ytproudltd@gmail.com').toLowerCase()
        ],
      order_id: order_id || '',
      product_id: product?.id || '',
      type: 'transaction',
      createdAt: (transactionConv || productConv)?.createdAt || serverTimestamp(),
      lastMessage: '',
      lastMessageFrom: '',
      lastMessageTime: null
    };

    if (transactionConv) {
      // Update existing transaction with latest order/product info
      const docRef = doc(db, 'sellerbuyerconversations', transactionConv.id);
      const update = {
        ...conversationData,
        lastContactedAt: serverTimestamp(),
        // cleanup legacy fields
        seller_username: deleteField(),
        relatedProducts: deleteField(),
        status: deleteField(),
      };
      if (order_id) update.orders = arrayUnion(order_id);
      await setDoc(docRef, update, { merge: true });
      return transactionConv.id;
    }

    if (productConv) {
      // Promote product_chat -> transaction in place (keep same ID)
      const docRef = doc(db, 'sellerbuyerconversations', productConv.id);
      const update = {
        ...conversationData, 
        type: 'transaction',
        conversationState: 'transaction',
        promotedAt: serverTimestamp(),
        lastContactedAt: serverTimestamp(),
        // cleanup legacy fields on promote
        seller_username: deleteField(),
        relatedProducts: deleteField(),
        status: deleteField(),
      };
      if (order_id) update.orders = arrayUnion(order_id);
      await setDoc(docRef, update, { merge: true });
      return productConv.id;
    }

    const pairId = await generateOpaquePairId(buyerEmail, sellerEmail);
    const update = {
      ...conversationData, 
      type: 'transaction',
      conversationState: 'transaction',
      promotedAt: serverTimestamp(),
      lastContactedAt: serverTimestamp(),
    };
    if (order_id) update.orders = arrayUnion(order_id);
    await setDoc(doc(db, 'sellerbuyerconversations', pairId), update, { merge: true });
    return pairId;
  } catch (error) {
    throw error;
  }
} 